'use client';

import { c as _c } from "react/compiler-runtime";
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import toast from '@shared/utils/helpers/toast';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { client } from '../../../utils/trpc';
import Button from '../../common/Button';
import Confirmation from '../../common/Confirmation';
import Modal, { ModalContent, ModalFooter, ModalHeader } from '../../HOC/Modal';
import BoardTargetPicker, { type MoveTarget, soleMoveTarget } from './BoardTargetPicker';
type Props = {
  spaceSlug: string;
  /** The list whose cards move. */
  list: {
    id: number;
    name: string;
  };
  closeHandler: () => void;
};
const cardsLabel = (count: number) => `${count} ${count === 1 ? 'card' : 'cards'}`;

/** "Move all cards to another board" in a list's menu: every card of that
 *  list goes to one list of another board. Owner or moderator; the server
 *  checks it, and its board events update both boards. */
const MoveListToBoardModal = t0 => {
  const $ = _c(54);
  const {
    spaceSlug,
    list,
    closeHandler
  } = t0;
  const [picked, setTarget] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  let t1;
  if ($[0] !== list.id || $[1] !== spaceSlug) {
    t1 = {
      queryKey: ["boardMoveTargets", spaceSlug, list.id],
      queryFn: () => unwrapFromResult(client.board.moveTargets.query({
        spaceSlug,
        fromColumnId: list.id
      }))
    };
    $[0] = list.id;
    $[1] = spaceSlug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data
  } = useQuery(t1);
  let t2;
  if ($[3] !== data?.boards || $[4] !== picked) {
    t2 = picked ?? soleMoveTarget(data?.boards);
    $[3] = data?.boards;
    $[4] = picked;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const target = t2;
  const cardCount = data?.cardCount ?? 0;
  let board;
  let t3;
  if ($[6] !== data?.boards || $[7] !== target?.listId || $[8] !== target?.slug) {
    board = data?.boards.find(b => b.slug === target?.slug);
    t3 = board?.lists.find(l => l.id === target?.listId);
    $[6] = data?.boards;
    $[7] = target?.listId;
    $[8] = target?.slug;
    $[9] = board;
    $[10] = t3;
  } else {
    board = $[9];
    t3 = $[10];
  }
  const toList = t3;
  let t4;
  if ($[11] !== closeHandler || $[12] !== list.id || $[13] !== spaceSlug || $[14] !== target) {
    t4 = async () => {
      if (!target) {
        return;
      }
      setIsMoving(true);
      const [error, result] = await client.board.moveAllCards.mutate({
        spaceSlug,
        fromColumnId: list.id,
        toSpaceSlug: target.slug,
        toColumnId: target.listId
      });
      setIsMoving(false);
      if (error) {
        setConfirming(false);
        return toast(error.message, "", "error");
      }
      toast(`Moved ${cardsLabel(result.moved)}`);
      closeHandler();
    };
    $[11] = closeHandler;
    $[12] = list.id;
    $[13] = spaceSlug;
    $[14] = target;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const moveAll = t4;
  if (confirming && board && toList) {
    let t5;
    if ($[16] !== cardCount) {
      t5 = cardsLabel(cardCount);
      $[16] = cardCount;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    const t6 = `${t5} will leave "${list.name}" and go to "${toList.name}" on ${board.name}.`;
    let t7;
    if ($[18] !== cardCount) {
      t7 = cardsLabel(cardCount);
      $[18] = cardCount;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    const t8 = `Move ${t7}`;
    let t9;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = () => setConfirming(false);
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[21] !== isMoving || $[22] !== moveAll || $[23] !== t6 || $[24] !== t8) {
      t10 = <Confirmation title="Move all cards?" message={t6} actionButtonText={t8} variant="DANGER" actionButtonLoading={isMoving} cancelHandler={t9} confirmHandler={moveAll} />;
      $[21] = isMoving;
      $[22] = moveAll;
      $[23] = t6;
      $[24] = t8;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    return t10;
  }
  let t5;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <h2>Move all cards to another board</h2>;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== closeHandler) {
    t6 = <ModalHeader closeHandler={closeHandler}>{t5}</ModalHeader>;
    $[27] = closeHandler;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  const t7 = data?.boards;
  let t8;
  if ($[29] !== t7 || $[30] !== target) {
    t8 = <BoardTargetPicker boards={t7} value={target} onChange={setTarget} />;
    $[29] = t7;
    $[30] = target;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== list.name) {
    t9 = <p className="mt-4 text-sm text-textSecondary">Each card of "{list.name}" keeps its link, comments, files and tagged members. Tags that belong to this board are removed. AI sessions, worktrees and branches stay on this board's runner. Private, draft and archived cards stay here.</p>;
    $[32] = list.name;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== t8 || $[35] !== t9) {
    t10 = <ModalContent>{t8}{t9}</ModalContent>;
    $[34] = t8;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== closeHandler) {
    t11 = <Button text="Cancel" variant="SECONDARY" onClick={closeHandler} />;
    $[37] = closeHandler;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== cardCount || $[40] !== data) {
    t12 = data ? `Move ${cardsLabel(cardCount)}` : "Move cards";
    $[39] = cardCount;
    $[40] = data;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => setConfirming(true);
    $[42] = t13;
  } else {
    t13 = $[42];
  }
  const t14 = !target || cardCount === 0;
  let t15;
  if ($[43] !== t12 || $[44] !== t14) {
    t15 = <Button text={t12} variant="DANGER" onClick={t13} disabled={t14} />;
    $[43] = t12;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== t11 || $[47] !== t15) {
    t16 = <ModalFooter>{t11}{t15}</ModalFooter>;
    $[46] = t11;
    $[47] = t15;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  let t17;
  if ($[49] !== closeHandler || $[50] !== t10 || $[51] !== t16 || $[52] !== t6) {
    t17 = <Modal closeHandler={closeHandler} maxWidth={500}>{t6}{t10}{t16}</Modal>;
    $[49] = closeHandler;
    $[50] = t10;
    $[51] = t16;
    $[52] = t6;
    $[53] = t17;
  } else {
    t17 = $[53];
  }
  return t17;
};
export default MoveListToBoardModal;